import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { Prompt } from 'react-router';
import { t } from '@lingui/core/macro';
import { getNavigationConfirmation } from 'src/components/NavigationConfirm';

export type UnsavedChangesGuard = {
  prompt: ReactElement;
  leave: (fn: () => void) => void;
  /** Asks the same "leave without saving?" question as a blocked navigation; resolves true when clean. */
  confirmLeave: () => Promise<boolean>;
};

function unsavedChangesMessage(): string {
  return t`You have unsaved changes. Leave without saving?`;
}

/**
 * Warns before in-app navigation (react-router Prompt) and tab close/reload (beforeunload) while
 * `isDirty`. Wrap navigation that follows a successful save in `leave()` so it is never prompted.
 * `fn` must navigate synchronously: the bypass is reset as soon as `fn` returns. For an action that
 * leaves only after async work (e.g. impersonating), await `confirmLeave()` first, then navigate in `leave()`.
 */
export function useUnsavedChangesGuard(isDirty: boolean): UnsavedChangesGuard {
  const bypassRef = useRef(false);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const leave = useCallback((fn: () => void) => {
    bypassRef.current = true;
    try {
      fn();
    } finally {
      bypassRef.current = false;
    }
  }, []);

  const confirmLeave = useCallback(
    () => (isDirty ? new Promise<boolean>(resolve => getNavigationConfirmation(unsavedChangesMessage(), resolve)) : Promise.resolve(true)),
    [isDirty]
  );

  const prompt = <Prompt when={isDirty} message={() => (bypassRef.current ? true : unsavedChangesMessage())} />;

  return { prompt, leave, confirmLeave };
}

export default useUnsavedChangesGuard;
